import FullCalendar, { EventApi } from '@fullcalendar/react';
import { NextPage } from 'next';
import Image from 'next/image';
import React, { useCallback, useContext, useEffect, useRef } from 'react';
import {
  TimeZone,
  Filters,
  SelectedLocation,
  FlexCalSummary,
  Opening,
  Distance,
  ExternalCalendarEventSummary,
  Reservation,
  FirstAvailableOpening,
  FlexCalAdditionalField,
  ExternalCalendar as ExternalCalendarType,
  ExternalContact,
} from '../../../../utils/types';
import Calendar from '../../../../components/calendars/Calendar';
import CalendarHeader from '../../../../components/calendars/CalendarHeader';
import { useState } from 'react';
import CalendarSideBar from '../../../../components/calendars/CalendarSideBar';
import { DateTime } from 'luxon';
import MeetingForm from '../../../../components/calendars/MeetingForm';
import {
  FCInputDataType,
  LoadingOrigin,
  navHeaderClick,
  ViewType,
} from '../../../../utils/enums';
import getInitialDate from '../../../../utils/helpers/getInitialDate';
import formatScrollTime from '../../../../utils/helpers/formatScrollTime';
import useInfiniteOpenings from '../../../../utils/hooks/useInfiniteOpenings';
import useInfiniteReservations from '../../../../utils/hooks/useInfiniteReservations';
import useInfiniteExternalEvents from '../../../../utils/hooks/useInfiniteExternalEvents';
import getApi_Uri from '../../../../utils/helpers/getApiUri';
import filterFCInput from '../../../../utils/helpers/filterFCInput';
import findFirstAvailableOpening from '../../../../utils/helpers/findFirstAvailableOpening';
import { BsQuestionCircle } from 'react-icons/bs';
import { IoOptionsOutline } from 'react-icons/io5';
import { ClockLoader } from 'react-spinners';
import { LoadingContext } from '../../../../utils/contexts/loadingContext';
import {
  Button,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  Typography,
} from '@material-tailwind/react';
import Head from 'next/head';
import useSWR from 'swr';
import ExternalCalendarView from '../../../../components/calendars/ExternalCalendar';
import { useUser } from '@auth0/nextjs-auth0';
import { UserInfo } from '../../../../utils/interfaces';
import { isPayingUser } from '../../../../utils/helpers/userHelper';
import ReactGA from 'react-ga4';
import { getFlexStartAndEndDate } from '../../../../utils/helpers/getFlexStartAndEndDate';
import * as Sentry from '@sentry/nextjs';
import { useHasAvailableEvents } from '../../../../utils/hooks/useHasAvailableEvents';
import TravelAwareCalendar from '../../../../components/calendars/TravelAwareCalendar';

const MOBILE_SCREEN_SIZE = 960;

type Props = {
  flexCalSummary: FlexCalSummary;
  timeZones: TimeZone[];
  hostName: string;
  isMacOS: boolean;
  subscriberName: string;
  flexCalName: string;
};

const ParticipantView: NextPage<Props> = ({
  timeZones,
  flexCalSummary,
  hostName,
  isMacOS,
  subscriberName,
  flexCalName,
}) => {
  const { user } = useUser();
  const userInfo = user as UserInfo;
  const API_URI = getApi_Uri(hostName, isMacOS);
  const { loading, setLoading } = useContext(LoadingContext);
  const [isOpeningsEnd, setIsOpeningsEnd] = useState<boolean>(false);
  const openingsHook = useInfiniteOpenings(
    flexCalSummary,
    setIsOpeningsEnd,
    API_URI
  );
  const [isEventsEnd, setIsEventsEnd] = useState<boolean>(false);
  const eventsHook = useInfiniteExternalEvents(
    flexCalSummary,
    setIsEventsEnd,
    API_URI,
    flexCalSummary?.userId
  );
  const [isReservationsEnd, setIsReservationsEnd] = useState<boolean>(false);
  const [isTravelCalendarLoading, setIsTravelCalendarLoading] = useState(true);
  const reservationsHook = useInfiniteReservations(
    flexCalSummary,
    setIsReservationsEnd,
    API_URI
  );

  useEffect(() => {
    console.log(
      'Openings - isLoadingInitialData:',
      openingsHook.isLoadingInitialData
    );
    console.log('Openings - isLoadingMore:', openingsHook.isLoadingMore);
    if (!isOpeningsEnd && !openingsHook.isLoadingMore) {
      openingsHook.setSize(openingsHook.size + 1);
    }
  }, [openingsHook.openings]);
  useEffect(() => {
    console.log(
      'Events - isLoadingInitialData:',
      eventsHook.isLoadingInitialData
    );
    console.log('Events - isLoadingMore:', eventsHook.isLoadingMore);
    if (!isEventsEnd && !eventsHook.isLoadingMore) {
      eventsHook.setSize(eventsHook.size + 1);
    }
  }, [eventsHook.events]);
  useEffect(() => {
    console.log(
      'Reservations - isLoadingInitialData:',
      reservationsHook.isLoadingInitialData
    );
    console.log(
      'Reservations - isLoadingMore:',
      reservationsHook.isLoadingMore
    );
    if (!isReservationsEnd && !reservationsHook.isLoadingMore) {
      reservationsHook.setSize(reservationsHook.size + 1);
    }
  }, [reservationsHook.reservations]);
  const [firstAvailableOpening, setFirstAvailableOpening] =
    useState<FirstAvailableOpening>({ isFound: false, opening: undefined });

  const [isExternalExpanded, setIsExternalExpanded] = useState<boolean>(false);

  const [participantEvents, setParticipantEvents] = useState<
    ExternalCalendarEventSummary[] | undefined
  >(undefined);

  const [participantContacts, setParticipantContacts] = useState<
    ExternalContact[] | undefined
  >(undefined);

  const { data: additionalFields } = useSWR<FlexCalAdditionalField[]>(
    `/api/flexCals/additionalFields/getAdditionalFields/${flexCalSummary.lazyCalendarId}`
  );

  const { data: externalCalendars } = useSWR<ExternalCalendarType[]>(
    user && userInfo.accountDetails?.edition !== 6
      ? '/api/user/externalCalendars/getExternalCalendars'
      : null
  );

  //Shows or hides filters on mobile view
  const [showFlexCalFiltersMobile, setShowFlexCalFiltersMobile] =
    useState<boolean>(true);
  //Intitialize an empty timeZone object, can be changed to a system defualt in the future
  const defaultTZ: TimeZone = {
    bclId: 'Eastern Standard Time',
    tzdbId: 'America/New_York',
  };
  //Store system timeZone
  const getLocalTimeZone = () => {
    try {
      const zoneName = DateTime.local().zoneName;
      return timeZones.find((tz) => tz.tzdbId === zoneName);
    } catch (error) {
      console.warn(
        'DateTime.local().zoneName not supported on this platform:',
        error
      );
      Sentry.captureException(error);
    }
    return defaultTZ;
  };
  const localTimeZone = getLocalTimeZone();
  //Using React forwarding to pull FullCalendar api object up to top level from component
  const calendarRef: React.RefObject<FullCalendar> =
    useRef() as React.MutableRefObject<FullCalendar>;
  //The date range displayed in text format within the FullCalendar header
  const [currentRange, setCurrentRange] = useState<string>('');
  //Luxon DateTime object set from React DatePicker in CalendarHeader
  const [datePickerDate, setDatePickerDate] = useState<DateTime | undefined>(
    DateTime.now()
  );
  //The current FullCalendar view, can be Week or Day
  const [currentView, setCurrentView] = useState<ViewType>(ViewType.Week);
  //If the FullCalendar element is currently displaying the current date (Day view) or current week (Week view)
  const [isToday, setIsToday] = useState<boolean>(true);
  //If there should be previous navigable dates. Set to true because FullCalendar will always initially load to current day or week
  const [isPrevDisabled, setIsPrevDisabled] = useState<boolean>(true);
  //If there should be future navigable dates
  const [isNextDisabled, setIsNextDisabled] = useState<boolean>(false);
  //Timezone used for FullCalendar component. Uses system defualt if present
  const [selectedTimeZone, setSelectedTimeZone] = useState<TimeZone>(
    localTimeZone !== undefined ? localTimeZone : defaultTZ
  );
  const [currentViewStart, setCurrentViewStart] = useState<DateTime>(
    DateTime.now()
  );
  const [currentViewEnd, setCurrentViewEnd] = useState<DateTime>(
    DateTime.now()
  );
  //Object to filter events by event time lengths and locations
  const [filters, setFilters] = useState<Filters>({
    durations: [],
    physicalLocations: [],
    virtualLocations: [],
  });
  const [selectedLocation, setSelectedLocation] = useState<SelectedLocation>({
    id: 0,
    name: 'Please choose',
    locationType: undefined,
  });
  //If an event on the FullCalendar component has been selected
  const [isEventClicked, setIsEventClicked] = useState<boolean>(false);
  //The event that has been selected in the FullCalendar component
  const [clickedEvent, setClickedEvent] = useState<EventApi | undefined>(
    undefined
  );

  //Used to disable user from navigating before or after range for flex cal (Scheduling Links)

  const [canNavNext, setCanNavNext] = useState<boolean>(true);
  const [canNavPrev, setCanNavPrev] = useState<boolean>(false);

  const [openHelpDialog, setOpenHelpDialog] = useState(false);
  const handleOpenHelpDialog = () => setOpenHelpDialog(!openHelpDialog);

  const [hasEventsInCurrentView, setHasEventsInCurrentView] =
    useState<boolean>(false);
  //FullCalendar current view
  const view = calendarRef.current?.getApi().view;
  //React hook to change FullCalendar TZ when input TZ changes
  useEffect(() => {
    calendarRef.current
      ?.getApi()
      .setOption('timeZone', selectedTimeZone.tzdbId);
  }, [selectedTimeZone]);
  //React hook to change FullCalendar date when a datePicker input is detected
  useEffect(() => {
    const calendarApi = calendarRef.current?.getApi();
    calendarApi?.gotoDate(datePickerDate?.toJSDate());
    const range = calendarRef.current?.getApi().getCurrentData().viewTitle;
    if (range && range !== null) {
      setCurrentRange(range.toString().substring(0, range.indexOf(',')));
    }
  }, [datePickerDate]);

  const { start, end } = getFlexStartAndEndDate(flexCalSummary);
  //React hook to dynamically disable, enable Calendar Header nav tools based on case, triggered by a change in FullCalendar component 'title'
  useEffect(() => {
    //FC api undefined checker
    if (
      calendarRef.current?.getApi().getCurrentData().currentDate === undefined
    )
      return;
    if (view?.currentStart && view.currentEnd) {
      setCurrentViewStart(DateTime.fromJSDate(view?.currentStart));
      setCurrentViewEnd(DateTime.fromJSDate(view.currentEnd));
    }
    if (flexCalSummary.evergreen) {
      if (calendarRef.current?.getApi().view.type === 'timeGridDay') {
        //If this is an evergreen flexcal (Scheduling Link), DayView is selected and the current date is today => disable today button
        DateTime.fromJSDate(
          calendarRef.current?.getApi().getCurrentData().currentDate
        )
          .plus({ hours: 4 })
          .toISODate() === DateTime.now().toISODate()
          ? setIsToday(true)
          : setIsToday(false);
        //If this is an evergreen flexcal (Scheduling Link), DayView is selected and the current date is the end of the flexcal => disable next button
        DateTime.fromJSDate(
          calendarRef.current?.getApi().getCurrentData().currentDate
        )
          .plus({ hours: 4 })
          .toISODate() === DateTime.fromISO(flexCalSummary.endDate).toISODate()
          ? setIsNextDisabled(true)
          : setIsNextDisabled(false);
      } else {
        //If this is an evergreen flexcal (Scheduling Link), WeekView is selected and the current date is within the current week => disable today button
        DateTime.fromJSDate(
          calendarRef.current?.getApi().getCurrentData().currentDate
        )
          .plus({ days: 1, hours: 4 })
          .startOf('week')
          .toISODate() === DateTime.now().startOf('week').toISODate()
          ? setIsToday(true)
          : setIsToday(false);
        //Disable next button if this is an evergreen flexcal (Scheduling Link), WeekView is selected and the current date is within the same week as the end of the flexcal => disable next button
        DateTime.fromISO(flexCalSummary.endDate).endOf('week').toISODate() ===
        DateTime.fromJSDate(
          calendarRef.current?.getApi().getCurrentData().currentDate
        )
          .plus({ days: 1, hours: 4 })
          .endOf('week')
          .toISODate()
          ? setIsNextDisabled(true)
          : setIsNextDisabled(false);
      }
    } else {
      if (calendarRef.current?.getApi().view.type === 'timeGridDay') {
        //If this is a fixed date flexcal (Scheduling Link), DayView is selected and the current date is the initial date => disable prev button
        DateTime.fromJSDate(
          calendarRef.current?.getApi().getCurrentData().currentDate
        )
          .plus({ hours: 4 })
          .toISODate() === getInitialDate(flexCalSummary).toISODate()
          ? setIsPrevDisabled(true)
          : setIsPrevDisabled(false);
        //If this is a fixed date flexcal (Scheduling Link), DayView is selected and the current date is the end date => disable next button
        DateTime.fromJSDate(
          calendarRef.current?.getApi().getCurrentData().currentDate
        )
          .plus({ hours: 4 })
          .toISODate() === DateTime.fromISO(flexCalSummary.endDate).toISODate()
          ? setIsNextDisabled(true)
          : setIsNextDisabled(false);
      } else {
        //If this is a fixed date flexcal (Scheduling Link), WeekView is selected and the current date is in the same week as the initial date => disable prev button
        DateTime.fromJSDate(
          calendarRef.current?.getApi().getCurrentData().currentDate
        )
          .plus({ days: 1, hours: 4 })
          .startOf('week')
          .toISODate() ===
        getInitialDate(flexCalSummary).startOf('week').toISODate()
          ? setIsPrevDisabled(true)
          : setIsPrevDisabled(false);
        //If this is a fixed date flexcal (Scheduling Link), WeekView is selected and the current date is in the same week as the end date => disable next button
        DateTime.fromJSDate(
          calendarRef.current?.getApi().getCurrentData().currentDate
        )
          .plus({ days: 1, hours: 4 })
          .endOf('week')
          .toISODate() ===
        DateTime.fromISO(flexCalSummary.endDate).endOf('week').toISODate()
          ? setIsNextDisabled(true)
          : setIsNextDisabled(false);
      }
    }
    if (view?.currentStart !== undefined && view.currentEnd !== undefined) {
      const endDateValue = end.endOf('week').minus({ hours: 5 });

      setCanNavPrev(
        DateTime.fromJSDate(view.currentStart)
          .startOf('day')
          .diff(start, 'days').days >= 1
      );
      setCanNavNext(
        endDateValue.diff(
          DateTime.fromJSDate(view.currentEnd).startOf('day'),
          'days'
        ).days >= 1
      );
    }
  }, [view?.title]);
  //React hook to automatically disable prev button after today button is clicked on evergreen, triggered by today button click
  useEffect(() => {
    if (flexCalSummary.evergreen) {
      isToday ? setIsPrevDisabled(true) : setIsPrevDisabled(false);
    }
  }, [isToday]);
  //React hook to dynamically change FC component after event click
  useEffect(() => {
    //FC api null checker
    if (!clickedEvent?.start) return;
    //Reformat FC title to day view after event click and scroll to selected event time
    if (isEventClicked) {
      setIsPrevDisabled(false);
      calendarRef.current
        ?.getApi()
        .changeView('timeGridDay', clickedEvent.start);
      const range = calendarRef.current?.getApi().getCurrentData().viewTitle;
      setCurrentRange(range!.toString().substr(0, range!.indexOf(',')));
      calendarRef.current
        ?.getApi()
        .scrollToTime(
          formatScrollTime(
            clickedEvent?.start?.getUTCHours(),
            clickedEvent?.start?.getMinutes()
          )
        );
      //undo FC eventClicked format
    } else {
      setCurrentView(ViewType.Week);
      setClickedEvent(undefined);
      if (calendarRef?.current?.getApi() !== undefined) {
        const earliestTime = autoScroll(
          calendarRef.current.getApi().getEvents()
        );
        calendarRef.current?.getApi().scrollToTime(earliestTime);
      }
    }
  }, [isEventClicked]);
  useEffect(() => {
    const calendarApi = calendarRef.current?.getApi();
    if (currentView === ViewType.Day) {
      calendarApi?.changeView('timeGridDay');
    } else {
      calendarApi?.changeView('timeGridWeek');
    }
    const range = calendarRef.current?.getApi().getCurrentData().viewTitle;
    setCurrentRange(range!.toString().substr(0, range!.indexOf(',')));
  }, [currentView]);
  //Function to handle Calendar Header clicks
  const handleNavHeaderClick = (clickType: navHeaderClick) => {
    const calendarApi = calendarRef.current?.getApi();
    //FC api Prev after prev click
    if (clickType === navHeaderClick.Prev) {
      calendarApi?.prev();
    }
    //FC api Next after next click
    if (clickType === navHeaderClick.Next) {
      calendarApi?.next();
    }
    //FC api nav to today after today click
    if (clickType === navHeaderClick.Today) {
      calendarApi?.gotoDate(DateTime.now());
    }
    //FC api change view to day view and set state after day view click
    if (clickType === navHeaderClick.DayView) {
      setCurrentView(ViewType.Day);
    }
    //FC api change view to week view and set state after week view click
    if (clickType === navHeaderClick.WeekView) {
      setCurrentView(ViewType.Week);
    }

    const range = calendarApi?.getCurrentData().viewTitle;
    setCurrentRange(range!.toString().substr(0, range!.indexOf(',')));
  };

  //Hook to properly set available times/external events/auto-navigate loader
  useEffect(() => {
    console.log(
      'Loading external data - openings page:',
      openingsHook.currentLoadedPage
    );
    console.log(
      'Loading external data - events page:',
      eventsHook.currentLoadedPage
    );
    console.log(
      'Loading external data - reservations page:',
      reservationsHook.currentLoadedPage
    );
    const pageDataReady = Math.min(
      openingsHook.currentLoadedPage,
      eventsHook.currentLoadedPage,
      reservationsHook.currentLoadedPage
    );
    const pageDataReadyEndDate = flexCalSummary.evergreen
      ? DateTime.now()
          .startOf('week')
          .minus({ days: 1 })
          .plus({
            weeks: pageDataReady * 2,
          })
      : DateTime.fromISO(flexCalSummary.startDate).plus({
          weeks: pageDataReady * 2,
        });

    if (
      openingsHook.isLoadingInitialData ||
      reservationsHook.isLoadingInitialData ||
      eventsHook.isLoadingInitialData
    ) {
      console.log('Loading in progress...');
      setLoading({
        isloading: true,
        loadingOrigin: LoadingOrigin.externalEvents,
      });
    } else if (!firstAvailableOpening.isFound) {
      setLoading({
        isloading: false,
        loadingOrigin: LoadingOrigin.autoNavigate,
      });
    } else {
      setLoading({
        isloading: false,
        loadingOrigin: LoadingOrigin.externalEvents,
      });
    }

    if (!firstAvailableOpening.isFound) {
      setFirstAvailableOpening({
        ...firstAvailableOpening,
        opening: findFirstAvailableOpening(
          openingsHook.openings,
          eventsHook.events,
          reservationsHook.reservations,
          flexCalSummary.firstAvailableTimeInHours,
          selectedTimeZone,
          pageDataReadyEndDate
        ),
      });
    }
  }, [
    eventsHook.currentLoadedPage,
    openingsHook.currentLoadedPage,
    reservationsHook.currentLoadedPage,
    view?.title,
  ]);

  useEffect(() => {
    if (
      firstAvailableOpening.opening !== undefined &&
      !firstAvailableOpening.isFound
    ) {
      const calendarApi = calendarRef.current?.getApi();
      calendarApi?.gotoDate(firstAvailableOpening.opening);
      setFirstAvailableOpening({ ...firstAvailableOpening, isFound: true });
      const range = calendarApi?.getCurrentData().viewTitle;
      if (range && typeof range === 'string') {
        const commaIndex = range.indexOf(',');
        setCurrentRange(
          commaIndex !== -1 ? range.substring(0, commaIndex) : range
        );
      } else {
        setCurrentRange(''); // or some default value
        console.warn('Calendar range is undefined:', range);
      }
      setLoading({
        isloading: false,
        loadingOrigin: LoadingOrigin.autoNavigate,
      });
    } else {
      // Delay 1 sec to close loader
      // setTimeout(() => {
      //   setLoading({
      //     isloading: false,
      //     loadingOrigin: LoadingOrigin.autoNavigate,
      //   });
      // }, 1000);
    }
  }, [firstAvailableOpening]);

  //Triggers when there is a change in events
  useEffect(() => {
    const events = calendarRef?.current?.getApi().getEvents();

    const hasEventsInCurrentViewCheck = events && events.length > 0;
    setHasEventsInCurrentView(!!hasEventsInCurrentViewCheck);
    //When on booking screen, a seperate autoscroll function will trigger for that specific event
    if (!isEventClicked) {
      //Scrolls to first event
      if (calendarRef?.current?.getApi() !== undefined) {
        const earliestTime = autoScroll(
          calendarRef.current.getApi().getEvents()
        );
        calendarRef.current?.getApi().scrollToTime(earliestTime);
      }
    }
  }, [
    calendarRef?.current?.getApi().getEvents(),
    openingsHook.openings,
    eventsHook.events,
    reservationsHook.reservations,
  ]);

  const [calendarHeight, setCalendarHeight] = useState<number | undefined>(
    undefined
  );
  const [calendarWidth, setCalendarWidth] = useState<number | undefined>(
    undefined
  );
  const calendarContainer = useCallback(
    (node: HTMLDivElement | null) => {
      if (node !== null) {
        try {
          const rect = node.getBoundingClientRect();
          setCalendarHeight(rect.height);
          setCalendarWidth(rect.width);
        } catch (error) {
          console.warn('Failed to get calendar container dimensions:', error);
          Sentry.captureException(error);
        }
      }
    },
    [isEventClicked]
  );

  const renderLoadingText = () => {
    console.log('Loading message origin:', loading?.loadingOrigin);
    switch (loading?.loadingOrigin) {
      case LoadingOrigin.externalEvents:
        return `Checking ${flexCalSummary.displayName}'s schedule. Please be patient...`;
      default:
        return `Finding ${flexCalSummary.displayName}'s first opening`;
    }
  };

  const toggleShowFlexCalFiltersMobile = () => {
    setShowFlexCalFiltersMobile(!showFlexCalFiltersMobile);
  };

  useEffect(() => {
    if (userInfo !== undefined) {
      ReactGA.set({
        username: userInfo.userPreferences?.userName,
        email: userInfo.userPreferences?.email,
        edition: userInfo.accountDetails?.edition,
        firstName: userInfo.userPreferences?.firstName,
        lastName: userInfo.userPreferences?.lastName,

        // Other relevant user information
      });
    }
  }, [userInfo]);

  const isLoading =
    loading?.isloading &&
    (loading.loadingOrigin === LoadingOrigin.externalEvents ||
      loading.loadingOrigin === LoadingOrigin.autoNavigate);

  const { hasAvailableEvent } = useHasAvailableEvents({
    flexCal: flexCalSummary,
    flexCalOpenings: filterFCInput(
      start,
      end,
      openingsHook.openings,
      FCInputDataType.openings
    ) as Opening[],
    externalEvents: filterFCInput(
      start,
      end,
      eventsHook.events,
      FCInputDataType.externalEvents
    ) as ExternalCalendarEventSummary[],
    participantEvents: filterFCInput(
      start,
      end,
      participantEvents,
      FCInputDataType.externalEvents
    ) as ExternalCalendarEventSummary[],
    reservations: filterFCInput(
      start,
      end,
      reservationsHook.reservations,
      FCInputDataType.reservations
    ) as Reservation[],
    filters,
    timeZone: selectedTimeZone,
    selectedLocation,
    startDate: start.toISO(),
    endDate: end.toISO(),
  });

  const renderNoEventsMessage = () => {
    if (!flexCalSummary.evergreen && isTravelCalendarLoading) {
      return '';
    }
    if (!hasEventsInCurrentView && !hasAvailableEvent) {
      return 'There are no open times on this scheduler';
    }

    if (!hasEventsInCurrentView) {
      return `There are no open times for this ${
        currentView === ViewType.Day ? 'day' : 'week'
      }`;
    }

    return '';
  };

  const noEventsMessage = renderNoEventsMessage();
  // grabs information on if the current user is a premium user
  const isPremium = isPayingUser(userInfo);

  return (
    <div className="flex flex-col h-full bg-white lg:h-screen lg:bg-neutral-grey">
      <Head>
        <title>{flexCalSummary.name}</title>
      </Head>
      <div className="flex flex-row items-center justify-start gap-2 pt-3 lg:items-center lg:p-0">
        <div className="p-0 lg:px-5 lg:py-2">
          <Image src="/confirmed_logo.svg" width={150} height={50} />
        </div>
        <h1 className="p-0 -ml-16 text-xl font-bold lg:pb-2 lg:-ml-20 lg:text-4xl justify-self-end">
          {flexCalSummary?.name}
        </h1>
        <div className="flex flex-row items-center gap-2 m-0 ml-auto mr-6 lg:block">
          <IoOptionsOutline
            className="m-0 ml-auto hover:cursor-pointer lg:hidden"
            size={35}
            onClick={toggleShowFlexCalFiltersMobile}
          ></IoOptionsOutline>
          <BsQuestionCircle
            className="m-0 ml-auto lg:block hover:cursor-pointer"
            color={'#40978F'}
            size={45}
            onClick={handleOpenHelpDialog}
          />
        </div>
        <Dialog
          open={openHelpDialog}
          handler={handleOpenHelpDialog}
          size={'xl'}
          className="overflow-auto h-auto max-h-[90%]"
        >
          <DialogHeader>
            <a href={'https://www.ConfirmedApp.com'} target="_blank">
              {' '}
              Tips: Confirming the meeting that best fits your needs.{' '}
            </a>
          </DialogHeader>
          <DialogBody divider className="flex flex-col gap-y-2">
            <Typography>
              This page automatically shows all options for Meeting Length,
              Location (place or platform), dates & timeslots that the calendar
              owner is making available for you to choose.
            </Typography>
            <Typography variant="small">
              • You can choose a time first — or you can filter the available
              times based on the location or meeting length you desire; then
              click to select your preferred time.
            </Typography>
            <Typography variant="small">
              • Choosing a time will open a panel for you to enter remaining
              info. Once you include all required info, you can click button to
              Confirm. You'll see a page where you can click to post on your
              calendar; and you'll receive an email address with full info that
              you can post on your calendar.
            </Typography>
            <Typography>
              IMPORTANT: If you have your own Confirmed account, your meeting
              will automatically show up on your calendar. If you do not have
              your own Confirmed account, you MUST use the buttons to add it to
              your calendar, or it will not be added.
            </Typography>
            <Typography variant="small">
              • If you want more people to join you, add them as "Additional
              Attendees". They will receive confirmations with complete info
              that they can post on their calendars by clicking a button.
            </Typography>
            <Typography variant="small">
              • You may change the time zone in which the slots appear by
              selecting the Time Zone control; then typing the time zone you
              desire to see.
            </Typography>
            <Typography variant="lead">
              As soon as you see the success message, the person on the other
              end (and all attendees) will receive the confirmation of your
              meeting with full info.
            </Typography>
            <Typography variant="small">
              Thanks for using Confirmed to schedule this meeting/event. Once
              you confirm this meeting, if you don't have a Confirmed account,
              you may learn more at{' '}
              <a
                href={'https://www.ConfirmedApp.com'}
                target="_blank"
                className="text-blue-900"
              >
                https://www.ConfirmedApp.com
              </a>
              .
            </Typography>
            <Typography variant="small">
              Confirmed does more than just scheduling. It puts artificial
              intelligence and cognitive science at your fingertips to help you
              reclaim time and refocus it on the people and activities that spur
              your success. Individual and group accounts available.
            </Typography>
          </DialogBody>
          <DialogFooter>
            <Button
              variant="gradient"
              color="teal"
              onClick={handleOpenHelpDialog}
            >
              <span>Close</span>
            </Button>
          </DialogFooter>
        </Dialog>
      </div>
      <div className="flex flex-col p-4 lg:flex-row h-full lg:h-[calc(100%-84px)] lg:p-0">
        {!isEventClicked && (
          <ExternalCalendarView
            flexCalSummary={flexCalSummary}
            setParticipantEvents={setParticipantEvents}
            setContacts={setParticipantContacts}
            externalCalendars={externalCalendars}
            isExpanded={isExternalExpanded}
            setIsExpanded={setIsExternalExpanded}
          />
        )}
        <div className={`lg:w-1/6 w-full ${isEventClicked ? 'hidden' : ''}`}>
          <CalendarSideBar
            timeZones={timeZones}
            selectedTimeZone={selectedTimeZone}
            setSelectedTimeZone={setSelectedTimeZone}
            filters={filters}
            setFilters={setFilters}
            selectedLocation={selectedLocation}
            setSelectedLocation={setSelectedLocation}
            showFlexCalFiltersMobile={showFlexCalFiltersMobile}
          />
        </div>
        <div
          className={`relative lg:mx-5 p-5 rounded-3xl w-full lg:block flex flex-col overflow-hidden lg:h-full ${
            loading?.isloading &&
            loading.loadingOrigin === LoadingOrigin.externalEvents
              ? 'pointer-events-none'
              : ''
          }  ${
            isEventClicked
              ? 'lg:w-1/6 bg-confirmed-secondary h-[240px] lg:h-full'
              : 'lg:w-5/6 bg-light-grey h-[calc(90vh-84px)]'
          } ${
            !isEventClicked &&
            isExternalExpanded &&
            (isPayingUser(userInfo)
              ? 'h-[calc(70vh-84px)] lg:h-full'
              : 'h-[calc(74vh-84px)] lg:h-full')
          }`}
          ref={calendarContainer}
        >
          {!isLoading && !isTravelCalendarLoading && noEventsMessage && (
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-40 flex flex-col items-center gap-3 text-xl text-confirmed-secondary">
              {noEventsMessage}
            </div>
          )}
          {isLoading && !isTravelCalendarLoading && (
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-40 flex flex-col items-center gap-3 text-xl text-confirmed-secondary text-center">
              <ClockLoader color="#41978F" />
              <h3>{renderLoadingText()}</h3>
            </div>
          )}
          <CalendarHeader
            handleNavHeaderClick={handleNavHeaderClick}
            currentRange={currentRange}
            currentView={currentView}
            datePickerDate={datePickerDate}
            setDatePickerDate={setDatePickerDate}
            flexCal={flexCalSummary}
            isPrevDisabled={isPrevDisabled}
            isNextDisabled={isNextDisabled}
            isEventClicked={isEventClicked}
            canNavNext={canNavNext}
            canNavPrev={canNavPrev}
          />
          {/* Show TravelAwareCalendar only if user is premium AND calendar is fixed date (not evergreen) */}
          {isPremium && !flexCalSummary.evergreen ? (
            <TravelAwareCalendar
              flexCal={flexCalSummary}
              flexCalOpenings={
                filterFCInput(
                  currentViewStart,
                  currentViewEnd,
                  openingsHook.openings,
                  FCInputDataType.openings
                ) as Opening[]
              }
              externalEvents={
                filterFCInput(
                  currentViewStart,
                  currentViewEnd,
                  eventsHook.events,
                  FCInputDataType.externalEvents
                ) as ExternalCalendarEventSummary[]
              }
              reservations={
                filterFCInput(
                  currentViewStart,
                  currentViewEnd,
                  reservationsHook.reservations,
                  FCInputDataType.reservations
                ) as Reservation[]
              }
              timeZone={selectedTimeZone}
              filters={filters}
              setFilters={setFilters}
              isEventClicked={isEventClicked}
              setIsEventClicked={setIsEventClicked}
              clickedEvent={clickedEvent}
              setClickedEvent={setClickedEvent}
              selectedLocation={selectedLocation}
              onDateRangeChange={setCurrentRange}
              setCurrentView={setCurrentView}
              calendarHeight={calendarHeight}
              calendarWidth={calendarWidth}
              participantEvents={
                filterFCInput(
                  currentViewStart,
                  currentViewEnd,
                  participantEvents,
                  FCInputDataType.externalEvents
                ) as ExternalCalendarEventSummary[]
              }
              ref={calendarRef}
              externalCalendars={externalCalendars}
              userLocation={userInfo}
              maxTravelTimeMinutes={60}
              flexCalName={flexCalName}
              subscriberName={subscriberName}
              setIsTravelCalendarLoading={setIsTravelCalendarLoading}
            />
          ) : (
            <Calendar
              flexCal={flexCalSummary}
              flexCalOpenings={
                filterFCInput(
                  currentViewStart,
                  currentViewEnd,
                  openingsHook.openings,
                  FCInputDataType.openings
                ) as Opening[]
              }
              externalEvents={
                filterFCInput(
                  currentViewStart,
                  currentViewEnd,
                  eventsHook.events,
                  FCInputDataType.externalEvents
                ) as ExternalCalendarEventSummary[]
              }
              reservations={
                filterFCInput(
                  currentViewStart,
                  currentViewEnd,
                  reservationsHook.reservations,
                  FCInputDataType.reservations
                ) as Reservation[]
              }
              timeZone={selectedTimeZone}
              filters={filters}
              setFilters={setFilters}
              isEventClicked={isEventClicked}
              setIsEventClicked={setIsEventClicked}
              clickedEvent={clickedEvent}
              setClickedEvent={setClickedEvent}
              selectedLocation={selectedLocation}
              setCurrentView={setCurrentView}
              calendarHeight={calendarHeight}
              calendarWidth={calendarWidth}
              participantEvents={
                filterFCInput(
                  currentViewStart,
                  currentViewEnd,
                  participantEvents,
                  FCInputDataType.externalEvents
                ) as ExternalCalendarEventSummary[]
              }
              ref={calendarRef}
              externalCalendars={externalCalendars}
            />
          )}
        </div>
        <div
          className={`lg:p-5 lg:mr-5 lg:mt-0 rounded-3xl bg-light-grey lg:w-5/6 w-full lg:overflow-y-auto lg:h-full ${
            isEventClicked ? 'lg:p-0 mt-4 h-fit' : 'hidden'
          }`}
        >
          <MeetingForm
            clickedEvent={clickedEvent}
            setIsEventClicked={setIsEventClicked}
            flexCal={flexCalSummary}
            filteredLocation={selectedLocation}
            timeZone={selectedTimeZone}
            apiUri={API_URI}
            additionalFields={additionalFields?.filter(
              (af) =>
                af.additionalFieldStatus === 1 || af.additionalFieldStatus === 2
            )}
            contacts={participantContacts}
            userInfo={userInfo}
          />
        </div>
        {/* David asked for this to be removed but I have a feeling this will come back */}
        {/* <p
          className={`px-12 py-4 text-sm lg:hidden sm:hidden ${
            isEventClicked ? 'hidden' : ''
          }`}
        >
          Powered by{' '}
          <a
            href="https://confirmedapp.com"
            className="text-confirmed-secondary"
            target="_blank"
          >
            {' '}
            <img
              src="/favicon.ico"
              alt="Confirmed Logo"
              className="inline ml-1"
              width={20}
              height={20}
            />
            ConfirmedApp.com
          </a>
        </p> */}
      </div>
    </div>
  );
};

export default ParticipantView;

export async function getServerSideProps(context: any) {
  const { query, req } = context;
  const { subscriberName, flexcal } = query;
  const flexCalSummaryRes = await fetch(
    `${process.env.BASE_URL}/api/flexCals/getSummary/${flexcal}?subscriberName=${subscriberName}`
  );
  const flexCalSummary = await flexCalSummaryRes.json();
  if (!flexCalSummary) {
    Sentry.captureEvent({
      message: 'Scheduler not found!',
      level: Sentry.Severity.Warning,
      extra: {
        section: 'scheduler',
        step: 'scheduler-no-found',
        query,
      },
    });
    return { notFound: true };
  }
  //Fetch timezones data from NextJS server
  const tzRes = await fetch(`${process.env.BASE_URL}/api/timezones`);
  const timeZones = await tzRes.json();

  const hostName = req.headers.host;

  //gets operating system
  const isMacOS = req.rawHeaders.includes('"macOS"');

  const flexCalName = flexcal;

  return {
    props: {
      flexCalSummary,
      timeZones,
      hostName,
      isMacOS,
      subscriberName,
      flexCalName,
    },
  };
}

const autoScroll = (events: EventApi[]) => {
  if (!events.length || !events[0].extendedProps?.interactive) {
    return formatScrollTime(
      events.length !== 0
        ? Math.min(
            ...events.map((ev) => (ev.start ? ev.start?.getUTCHours() : 8))
          )
        : 8,
      0
    );
  } else {
    return formatScrollTime(
      events.length !== 0
        ? Math.min(...events.map((ev) => (ev.start ? ev.start.getHours() : 8)))
        : 8,
      0
    );
  }
};
